import type { LngLat, ShapeStyle } from "../types";

/** Épaisseur d'un mur dont le trait est en pixels : une taille à l'écran ne dit rien de sa taille au sol. */
export const DEFAULT_LINE_WALL_THICKNESS_METERS = 0.2;

/** Au-delà, un angle aigu pousserait la pointe de l'onglet trop loin : on coupe l'angle (biseau). */
const MAX_MITER_RATIO = 4;

const METERS_PER_DEG_LAT = 111_320;

/** Épaisseur du mur d'une ligne : celle de son trait quand elle est en mètres. */
export function lineWallThicknessMeters(style: ShapeStyle | undefined): number {
  const width = style?.borderWidth;
  return style?.borderWidthUnit === "m" && width && width > 0 ? width : DEFAULT_LINE_WALL_THICKNESS_METERS;
}

/**
 * Contour fermé de la bande d'épaisseur `thicknessMeters` centrée sur la polyligne : un côté à
 * l'aller, l'autre au retour. Les angles sont raccordés en onglet, ou en biseau quand ils sont trop
 * aigus. Calculé dans un plan local en mètres, assez juste à l'échelle d'un plan. `null` sous deux
 * points distincts.
 */
export function lineWallRing(coordinates: LngLat[], thicknessMeters: number): LngLat[] | null {
  const points = coordinates.filter((p, i) => i === 0 || p[0] !== coordinates[i - 1]![0] || p[1] !== coordinates[i - 1]![1]);
  if (points.length < 2) return null;

  const [lng0, lat0] = points[0]!;
  const metersPerDegLng = METERS_PER_DEG_LAT * Math.cos((lat0 * Math.PI) / 180);
  const local = points.map(([lng, lat]) => ({ x: (lng - lng0) * metersPerDegLng, y: (lat - lat0) * METERS_PER_DEG_LAT }));
  const toLngLat = (x: number, y: number): LngLat => [lng0 + x / metersPerDegLng, lat0 + y / METERS_PER_DEG_LAT];

  // Normale à gauche de chaque segment.
  const normals = local.slice(1).map((b, i) => {
    const a = local[i]!;
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    return { x: -(b.y - a.y) / length, y: (b.x - a.x) / length };
  });

  const half = thicknessMeters / 2;
  const left: LngLat[] = [];
  const right: LngLat[] = [];
  const offset = (p: { x: number; y: number }, n: { x: number; y: number }, scale: number) => {
    left.push(toLngLat(p.x + n.x * scale, p.y + n.y * scale));
    right.push(toLngLat(p.x - n.x * scale, p.y - n.y * scale));
  };

  local.forEach((p, i) => {
    const before = normals[i - 1];
    const after = normals[i];
    if (!before || !after) return offset(p, (before ?? after)!, half);

    const miter = { x: before.x + after.x, y: before.y + after.y };
    const miterLength = Math.hypot(miter.x, miter.y);
    const cos = miterLength / 2;
    if (miterLength < 1e-9 || 1 / cos > MAX_MITER_RATIO) {
      offset(p, before, half);
      offset(p, after, half);
      return;
    }
    offset(p, { x: miter.x / miterLength, y: miter.y / miterLength }, half / cos);
  });

  return [...left, ...right.reverse(), left[0]!];
}
